import React, { FC, useContext, useState, useCallback, useMemo } from "react";
import { Password } from "primereact/password";
import { Context } from "../../../index";
import { AppContext } from "../../../App";
import ButtonBase from "../../buttons/ButtonBase/ButtonBase";
import CrossImg from "../../../assets/images/cross.svg";
import "./PasswordChange.css";

interface IPasswordChangeProps {
    onClose: () => void;
}

const MIN_PASSWORD_LENGTH = 8;

const PasswordChange: FC<IPasswordChangeProps> = ({ onClose }) => {
    const { store } = useContext(Context);
    const { showToast } = useContext(AppContext);

    const [passwords, setPasswords] = useState({
        current: { value: "", error: "" },
        new: { value: "", error: "" },
        confirm: { value: "", error: "" }
    });

    const fieldConfig = useMemo(() => [
        {
            id: "current-password",
            label: "Текущий пароль",
            key: "current" as const,
            placeholder: "* * * * *"
        },
        {
            id: "new-password",
            label: "Новый пароль",
            key: "new" as const,
            placeholder: "* * * * *"
        },
        {
            id: "new-password-confirm",
            label: "Подтверждение нового пароля",
            key: "confirm" as const,
            placeholder: "* * * * *"
        }
    ], []);

    const validatePassword = useCallback((value: string): string => {
        if (!value || value.length === 0) {
            return "Введите пароль";
        }
        if (value.length < MIN_PASSWORD_LENGTH) {
            return `Минимальная длина пароля: ${MIN_PASSWORD_LENGTH} символов`;
        }
        return "";
    }, []);

    const validatePasswordMatch = useCallback((newPassword: string, confirmPassword: string): string => {
        if (newPassword !== confirmPassword) {
            return "Пароли не совпадают";
        }
        return "";
    }, []);

    const handlePasswordChange = useCallback((key: keyof typeof passwords, value: string) => {
        setPasswords(prev => ({
            ...prev,
            [key]: { ...prev[key], value, error: "" }
        }));
    }, []);

    const handleBlur = useCallback((key: keyof typeof passwords) => {
        const error = validatePassword(passwords[key].value);
        setPasswords(prev => ({
            ...prev,
            [key]: { ...prev[key], error }
        }));
    }, [passwords, validatePassword]);

    const handleConfirmBlur = useCallback(() => {
        const matchError = validatePasswordMatch(passwords.new.value, passwords.confirm.value);
        setPasswords(prev => ({
            ...prev,
            confirm: { ...prev.confirm, error: matchError }
        }));
    }, [passwords.new.value, passwords.confirm.value, validatePasswordMatch]);

    const onSave = useCallback(async () => {
        const errors = {
            current: validatePassword(passwords.current.value),
            new: validatePassword(passwords.new.value),
            confirm: validatePasswordMatch(passwords.new.value, passwords.confirm.value)
        };

        setPasswords(prev => ({
            current: { ...prev.current, error: errors.current },
            new: { ...prev.new, error: errors.new },
            confirm: { ...prev.confirm, error: errors.confirm }
        }));

        const hasErrors = Object.values(errors).some(error => error !== "");
        if (hasErrors) return;

        try {
            const res = await store.changePassword({
                oldPassword: passwords.current.value.trim() || undefined,
                newPassword: passwords.new.value,
                newPasswordConfirm: passwords.confirm.value,
            });

            if (res.status === 200) {
                setPasswords({
                    current: { value: "", error: "" },
                    new: { value: "", error: "" },
                    confirm: { value: "", error: "" }
                });
                showToast("Пароль успешно изменен");
            } else if (res.status === 403) {
                setPasswords(prev => ({
                    ...prev,
                    current: { ...prev.current, error: "Неверный пароль" }
                }));
            } else {
                showToast(`Ошибка при обновлении пароля: ${res?.data?.message}`);
            }
        } catch (err) {
            showToast(`Ошибка при смене пароля, пароль не изменен: ${err}`);
        }
    }, [passwords, store, showToast, validatePassword, validatePasswordMatch]);

    const renderPasswordField = useCallback((field: typeof fieldConfig[0]) => (
        <div key={field.id} className="password-change-field-block">
            <label className="input_label" htmlFor={field.id}>
                {field.label}
            </label>
            <Password
                id={field.id}
                value={passwords[field.key].value}
                onBlur={() => field.key === "confirm" ? handleConfirmBlur() : handleBlur(field.key)}
                className="input_form_login"
                feedback={false}
                toggleMask
                placeholder={field.placeholder}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                    handlePasswordChange(field.key, e.target.value)
                }
            />
            <p className="error_message">
                {passwords[field.key].error || "\u00A0"}
            </p>
        </div>
    ), [passwords, handleBlur, handleConfirmBlur, handlePasswordChange]);

    return (
        <div className="personal-settings-container-container">
            <div className="personal-settings-container">
                <div className="password-change-header">
                    <h1 className="header-text">Смена пароля</h1>
                    <button 
                        className="button-base personal-settings-close-button" 
                        onClick={onClose}
                        aria-label="Закрыть"
                    >
                        <img src={CrossImg} alt="Закрыть" />
                    </button>
                </div>

                <div className="password-change-content">
                    {renderPasswordField(fieldConfig[0])}
                    
                    <div className="password-change-new-fields">
                        {fieldConfig.slice(1).map(renderPasswordField)}
                    </div>
                </div>

                <div className="password-change-controls">
                    <ButtonBase
                        textContent="Сохранить"
                        onClickFunc={onSave}
                        optionalClasses="blue-button white-font"
                        dataTestId="button-change_password_form-save"
                    />
                    <ButtonBase
                        textContent="Отмена"
                        onClickFunc={onClose}
                        optionalClasses="gray-button blue-font"
                        dataTestId="button-change_password_form-cancel"
                    />
                </div>
            </div>
        </div>
    );
};

export default React.memo(PasswordChange);
        // <div className="personal-settings-container-container">
        //     <div className="personal-settings-container">
        //         <div style={{ display: "flex", justifyContent: "space-between" }}>
        //             <h1 className="header-text">Смена пароля</h1>
        //             <button className="button-base personal-settings-close-button" onClick={onClose}>
        //                 <img src={CrossImg}></img>
        //             </button>
        //         </div>
        //         <div style={{ paddingTop: "24px", height: "100%" }} id="user-personal-settings-password-change-block">
        //             <div className="password-change-field-block">
        //                 <label className="input_label" htmlFor="current-password">
        //                     Текущий пароль
        //                 </label>
        //                 <Password
        //                     id="current-password"
        //                     value={password}
        //                     // onBlur={() => validatePassword(password, setPasswordError)}
        //                     className="input_form_login"
        //                     feedback={false}
        //                     toggleMask
        //                     placeholder="* * * * *"
        //                     onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
        //                 />
        //                 <p className="error_message">{passwordError ?? "\u00A0"}</p>
        //             </div>
        //             <div style={{ marginTop: "32px" }}>
        //                 <div className="password-change-field-block">
        //                     <label className="input_label" htmlFor="new-password">
        //                         Новый пароль
        //                     </label>
        //                     <Password
        //                         id="new-password"
        //                         value={newPassword}
        //                         onBlur={() => validatePassword(newPassword, setNewPasswordError)}
        //                         className="input_form_login"
        //                         feedback={false}
        //                         toggleMask
        //                         placeholder="* * * * *"
        //                         onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
        //                     />
        //                     <p className="error_message">{newPasswordError ?? "\u00A0"}</p>
        //                 </div>
        //                 <div className="password-change-field-block">
        //                     <label className="input_label" htmlFor="new-password-confirm">
        //                         Подтверждение нового пароля
        //                     </label>
        //                     <Password
        //                         id="new-password-confirm"
        //                         value={newPasswordConfirm}
        //                         onBlur={() => validatePassword(newPasswordConfirm, setNewPasswordConfirmError)}
        //                         className="input_form_login"
        //                         feedback={false}
        //                         toggleMask
        //                         placeholder="* * * * *"
        //                         onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        //                             setNewPasswordConfirm(e.target.value)
        //                         }
        //                     />
        //                     <p className="error_message">{newPasswordConfirmError ?? "\u00A0"}</p>
        //                 </div>
        //             </div>
        //         </div>
        //         <div className="password-change-field-block">
        //             <div className="password-change-form-controls">
        //                 <div>
        //                     <ButtonBase
        //                         textContent="Сохранить"
        //                         onClickFunc={onSave}
        //                         optionalClasses="blue-button white-font"
        //                         dataTestId="button-change_password_form-save"
        //                     />
        //                 </div>
        //                 <div>
        //                     <ButtonBase
        //                         textContent="Отмена"
        //                         onClickFunc={onClose}
        //                         optionalClasses="gray-button blue-font"
        //                         dataTestId="button-change_password_form-cancel"
        //                     />
        //                 </div>
        //             </div>
        //         </div>
        //     </div>
        // </div>
